import React, { createContext, useContext, useState, useEffect } from "react";
import AuthenticationService from "../Services/Auth/AuthenticationService";
import { AxiosError } from "axios";
const authService = new AuthenticationService();

// Define the AuthContext type
interface AuthContextType {
    authService: AuthenticationService;
    //have isAuthenticated as null so you can have a state before we auth
    isAuthenticated: boolean | null;
    setIsAuthenticated: (value: boolean) => void;
}

// Create the AuthContext
const AuthContext = createContext<AuthContextType>({
    authService,
    isAuthenticated: null,
    setIsAuthenticated: () => { },
});

// Custom hook for consuming the AuthContext
export const useAuth = () => useContext(AuthContext);

// AuthProvider component
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
    useEffect(() => {
        let validateIntervalId: NodeJS.Timeout;
        let tokenPollIntervalId: NodeJS.Timeout;

        const pollTokens = () => {
            const token = authService.getToken();
            const refreshToken = authService.getRefreshToken();

            // If both missing, instantly mark unauthenticated
            if (!token && !refreshToken) {
                setIsAuthenticated(false);
            }
        };

        const validateToken = async () => {
            const token = authService.getToken();
            const refreshToken = authService.getRefreshToken();

            if (!token && !refreshToken) {
                setIsAuthenticated(false);
                return;
            }

            const tryRefreshAndValidate = async () => {
                try {
                    console.log("Forbidden → trying refresh");
                    await authService.tryRefreshAuthTokenOrLogOut();
                    await authService.validateToken();
                    setIsAuthenticated(true);
                } catch {
                    console.log("Forbidden, refresh failed");
                    setIsAuthenticated(false);
                }
            };

            if (!token) {
                console.log("No token → attempting refresh");
                return tryRefreshAndValidate();
            }

            try {
                await authService.validateToken();
                setIsAuthenticated(true);
            } catch (error) {
                const axiosError = error as AxiosError;
                if (axiosError.response?.status === 403) {
                    await tryRefreshAndValidate();
                } else {
                    console.log("Validation failed with other error");
                    setIsAuthenticated(false);
                }
            }
        };

       
        validateToken();
        pollTokens();

        // light check every 1 sec
        tokenPollIntervalId = setInterval(pollTokens, 1000);

        // validate backend every 20 secs
        validateIntervalId = setInterval(validateToken, 20000);

        return () => {
            clearInterval(tokenPollIntervalId);
            clearInterval(validateIntervalId);
        };
    }, [authService]);
    return (
        <AuthContext.Provider value={{ authService, isAuthenticated, setIsAuthenticated }}>
            {children}
        </AuthContext.Provider>
    );
};
